<template>
  <modal 
    name="agreement-mark-multiple" 
    class="modal-inner modal-agreement-mark-multiple" 
    transition="pop-out" 
    :width="modalWidth" 
    :focus-trap="true" 
    height="auto" 
    :scrollable="true"
    @before-open="onBeforeOpen"
  >
    <span class="close-button" @click="hide('agreement-mark-multiple')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t('agreements.mark_multiple') }}
    </div>

    <div class="modal-body">
      <!-- Form Section (for dismiss/end actions) -->
      <div class="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">
          <BaseIcon icon="cog" class="mr-1" />
          {{ $t('agreements.bulk.action_settings') }}
        </h4>
        
        <!-- Date Field -->
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('agreements.bulk.action_date') }} *
          </label>
          <input 
            type="date"
            v-model="actionDate"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            :class="{ 'border-red-500': errors.date }"
          />
          <span v-if="errors.date" class="text-red-500 text-xs mt-1 block">{{ errors.date }}</span>
        </div>

        <!-- Billing Method -->
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('cancel_agreement.billing_method_label') }} *
          </label>
          <select 
            v-model="billingMethod"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            :class="{ 'border-red-500': errors.billing }"
          >
            <option value="">{{ $t('cancel_agreement.select_billing_method') }}</option>
            <option value="as_before">{{ $t('cancel_agreement.billing_as_before') }}</option>
            <option value="final_invoice">{{ $t('cancel_agreement.final_invoice') }}</option>
            <option value="no_invoice">{{ $t('cancel_agreement.no_invoice') }}</option>
          </select>
          <span v-if="errors.billing" class="text-red-500 text-xs mt-1 block">{{ errors.billing }}</span>
        </div>

        <!-- Notes (optional, for cancel/end) -->
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('agreements.bulk.notes') }}
            <span class="text-xs text-gray-500 ml-1">({{ $t('cancel_agreement.optional') }})</span>
          </label>
          <textarea 
            v-model="notes"
            rows="2"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            :placeholder="$t('agreements.bulk.notes_placeholder')"
          ></textarea>
        </div>

        <!-- PDF Upload -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            <BaseIcon icon="file-pdf" class="mr-1 text-red-500" />
            {{ $t('agreements.bulk.upload_pdf') }} *
          </label>
          <input 
            type="file" 
            accept=".pdf"
            @change="handleFileUpload"
            ref="fileInput"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            :class="{ 'border-red-500': errors.file }"
          />
          <span v-if="selectedFile" class="text-xs text-green-600 mt-1 block">
            <BaseIcon icon="check-circle" class="mr-1" />
            {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
          </span>
          <span v-if="errors.file" class="text-red-500 text-xs mt-1 block">{{ errors.file }}</span>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex flex-wrap gap-2 mb-4 p-2 bg-gray-50 rounded-lg">
        <button 
          class="flex-1 px-4 py-2 rounded-md font-medium text-sm transition-colors border-2 border-gray-800 text-gray-900 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="selectedAgreements.length === 0 || isProcessing"
          @click="dismissSelected"
        >
          <BaseIcon icon="ban" class="mr-1" />
          {{ $t('agreements.bulk.dismiss_all') }} ({{ selectedAgreements.length }})
        </button>
        <button 
          class="flex-1 px-4 py-2 rounded-md font-medium text-sm transition-colors border-2 border-gray-800 text-gray-900 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="selectedAgreements.length === 0 || isProcessing"
          @click="endSelected"
        >
          <BaseIcon icon="times-circle" class="mr-1" />
          {{ $t('agreements.bulk.end_all') }} ({{ selectedAgreements.length }})
        </button>
        <button 
          class="flex-1 px-4 py-2 rounded-md font-medium text-sm transition-colors border-2 border-gray-800 text-gray-900 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="selectedAgreements.length === 0 || isProcessing"
          @click="extendSelected"
        >
          <BaseIcon icon="sync-alt" class="mr-1" />
          {{ $t('agreements.bulk.extend_all') }} ({{ selectedAgreements.length }})
        </button>
      </div>

      <!-- Suggested Auto-Extend Section -->
      <div v-if="suggestedAutoExtend.length > 0" class="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
        <div class="flex items-center justify-between mb-2">
          <h4 class="text-sm font-semibold text-amber-800">
            <BaseIcon icon="exclamation-triangle" class="mr-1 text-amber-600" />
            {{ $t('agreements.bulk.suggested_auto_extend') }} ({{ suggestedAutoExtend.length }})
          </h4>
          <button 
            @click="selectAllSuggested"
            class="text-xs px-2 py-1 bg-amber-200 hover:bg-amber-300 text-amber-800 rounded transition-colors"
          >
            {{ $t('agreements.bulk.select_all_suggested') }}
          </button>
        </div>
        <p class="text-xs text-amber-700 mb-2">
          {{ $t('agreements.bulk.suggested_auto_extend_desc') }}
        </p>
        <div class="max-h-40 overflow-y-auto">
          <div 
            v-for="agreement in suggestedAutoExtend" 
            :key="'suggested-' + agreement._id"
            class="flex items-center gap-2 px-2 py-1.5 hover:bg-amber-100 rounded text-sm"
          >
            <input 
              type="checkbox"
              :id="'suggested-' + agreement._id"
              :value="agreement._id"
              v-model="selectedAgreements"
              class="w-4 h-4 text-amber-600 rounded"
            />
            <div class="flex-1 min-w-0">
              <span class="font-medium text-amber-800">{{ agreement.agreement_number }}</span>
              <span class="text-amber-600 text-xs ml-2">{{ agreement.customer_name }}</span>
            </div>
            <span class="text-xs px-2 py-0.5 rounded-full" :class="getExpiryBadgeClass(agreement)">
              {{ getExpiryLabel(agreement) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Filter/Search -->
      <div class="flex flex-wrap gap-2 mb-4 px-2">
        <input 
          v-model="searchQuery"
          type="text"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
          :placeholder="$t('agreements.search_placeholder')"
        />
        <select v-model="statusFilter" class="px-3 py-2 border border-gray-300 rounded-md text-sm">
          <option value="">{{ $t('agreements.all_statuses') }}</option>
          <option value="active">{{ $t('agreements.active') }}</option>
          <option value="active_old">{{ $t('agreements.active_old') }}</option>
          <option value="extended">{{ $t('agreements.extended') }}</option>
          <option value="pending">{{ $t('agreements.pending') }}</option>
          <option value="dismissed">{{ $t('agreements.dismissed') }}</option>
        </select>
      </div>

      <!-- Select all checkbox -->
      <div class="flex items-center gap-2 px-2 mb-2 pb-2 border-b">
        <input 
          type="checkbox"
          id="select-all"
          :checked="allFilteredSelected"
          @change="toggleSelectAll"
          class="w-4 h-4 text-blue-600 rounded"
        />
        <label for="select-all" class="text-sm font-medium text-gray-700">
          {{ $t('agreements.select_all') }} ({{ filteredAgreements.length }})
        </label>
      </div>

      <!-- Agreements list -->
      <div class="max-h-96 overflow-y-auto">
        <div 
          v-for="agreement in filteredAgreements" 
          :key="agreement._id"
          class="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 border-b border-gray-100"
        >
          <input 
            type="checkbox"
            :id="'agreement-' + agreement._id"
            :value="agreement._id"
            v-model="selectedAgreements"
            class="w-4 h-4 text-blue-600 rounded"
          />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-medium text-sm text-blue-600">{{ agreement.agreement_number }}</span>
              <span :class="getStatusBadgeClass(agreement.status)" class="px-2 py-0.5 text-xs rounded-full">
                {{ getStatusLabel(agreement.status) }}
              </span>
            </div>
            <div class="text-xs text-gray-500 truncate">
              {{ agreement.customer_name }} 
              <span v-if="agreement.contract_period">• {{ agreement.contract_period }}</span>
              <span v-if="agreement.rental_month_cost">• {{ formatAmount(agreement.rental_month_cost) }} kr/mån</span>
            </div>
          </div>
        </div>

        <div v-if="filteredAgreements.length === 0" class="text-center py-8 text-gray-500">
          {{ $t('agreements.no_agreements_found') }}
        </div>
      </div>

      <!-- Processing indicator -->
      <div v-if="isProcessing" class="mt-4 p-3 bg-blue-50 rounded-lg text-center">
        <BaseIcon icon="spinner" class="animate-spin mr-2" />
        {{ $t('agreements.processing') }}... ({{ processedCount }}/{{ selectedAgreements.length }})
      </div>
    </div>
  </modal>
</template> 

<script>
export default {
  name: 'AgreementMarkMultiple',
  props: {
    agreements: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedAgreements: [],
      searchQuery: '',
      statusFilter: '',
      isProcessing: false,
      processedCount: 0,
      selectedFile: null,
      actionDate: '',
      billingMethod: '',
      notes: '',
      errors: {
        file: '',
        date: '',
        billing: ''
      },
      allAgreements: [], // Store all agreements fetched from parent
      agreementAlarms: [] // Store alarm data from parent
    };
  }, 
  computed: {
    modalWidth() {
      if (window.innerWidth < 768) return "100%";
      return "700px";
    },
    minDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    /**
     * Agreements that should be suggested for auto-extension:
     * - "Cash" agreements (contract_period = 0) are never recommended
     * - All agreements where contract_start + contract_period < today
     * - Excludes active_old agreements (already have an extended child)
     * - Excludes returned/dismissed agreements (already ended)
     * - Used to identify expired agreements that need status updates
     */
    suggestedAutoExtend() {
      const now = new Date();
      const sourceAgreements = this.allAgreements.length > 0 ? this.allAgreements : this.agreements;
      
      return sourceAgreements.filter(agreement => {
        // Exclude active_old agreements (they already have an extended child)
        if (agreement.status === 'active_old') {
          return false;
        }
        
        // Exclude returned/dismissed agreements (they are ended)
        if (agreement.status === 'returned' || agreement.status === 'dismissed') {
          return false;
        }
        
        // Must have contract_start and contract_period
        if (!agreement.contract_start || !agreement.contract_period) {
          return false;
        }
        
        // Recommend cash agreements (contract_period = 0) for extension
        if (agreement.contract_period === 0 || agreement.contract_period === '0') {
          return false;
        }
        
        // Calculate expiry date
        const contractStart = new Date(agreement.contract_start);
        const periodMatch = agreement.contract_period.toString().match(/(\d+)/);
        if (!periodMatch) return false;
        
        const periodMonths = parseInt(periodMatch[1]);
        const expiryDate = new Date(contractStart);
        expiryDate.setMonth(expiryDate.getMonth() + periodMonths);
        
        // Show all agreements where contract has expired
        return expiryDate < now;
      });
    },
    filteredAgreements() {
      // Use allAgreements if available (fetched from parent), otherwise fall back to prop
      let filtered = this.allAgreements.length > 0 ? this.allAgreements : this.agreements;
      
      // Filter by status (only show actionable statuses)
      if (this.statusFilter) {
        filtered = filtered.filter(a => a.status === this.statusFilter);
      } else {
        // By default, show agreements that can be acted upon
        filtered = filtered.filter(a => 
          ['active', 'active_old', 'extended', 'pending', 'redrawn', 'dismissed'].includes(a.status) || !a.status
        );
      }
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(a => 
          (a.agreement_number && a.agreement_number.toLowerCase().includes(query)) ||
          (a.customer_name && a.customer_name.toLowerCase().includes(query))
        );
      }
      
      return filtered;
    },
    allFilteredSelected() {
      if (this.filteredAgreements.length === 0) return false;
      return this.filteredAgreements.every(a => this.selectedAgreements.includes(a._id));
    }
  },
  methods: {
    hide(name) {
      this.$modal.hide(name);
    },
    
    onBeforeOpen(event) {
      this.selectedAgreements = [];
      this.searchQuery = '';
      this.statusFilter = '';
      this.isProcessing = false;
      this.processedCount = 0;
      this.selectedFile = null;
      this.actionDate = '';
      this.billingMethod = '';
      this.notes = '';
      this.errors = { file: '', date: '', billing: '' };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      
      // Use allAgreements from parent if provided, otherwise fall back to prop
      if (event?.params?.allAgreements) {
        this.allAgreements = event.params.allAgreements;
      }
      
      // Store agreement alarms data from parent
      if (event?.params?.agreementAlarms) {
        this.agreementAlarms = event.params.agreementAlarms;
      }

      // Pre-select agreements that were checked in the main table
      if (event?.params?.preSelectedIds && event.params.preSelectedIds.length > 0) {
        this.selectedAgreements = [...event.params.preSelectedIds];
      }
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0];
      this.errors.file = '';
      
      if (file && file.type !== 'application/pdf') {
        this.errors.file = this.$t('agreements.bulk.error_invalid_file_type');
        this.selectedFile = null;
        return;
      }
      
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file && file.size > maxSize) {
        this.errors.file = this.$t('agreements.bulk.error_file_too_large');
        this.selectedFile = null;
        return;
      }
      
      this.selectedFile = file;
    },
    
    validateForm(action) {
      this.errors = { file: '', date: '', billing: '' };
      let isValid = true;
      
      // Only require these fields for dismiss and end actions
      if (action === 'dismiss' || action === 'end') {
        if (!this.selectedFile) {
          this.errors.file = this.$t('agreements.bulk.pdf_required');
          isValid = false;
        }
        
        if (!this.actionDate) {
          this.errors.date = this.$t('cancel_agreement.error_date_required');
          isValid = false;
        }
        
        if (!this.billingMethod) {
          this.errors.billing = this.$t('cancel_agreement.error_billing_required');
          isValid = false;
        }
      }
      
      return isValid;
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    
    toggleSelectAll() {
      if (this.allFilteredSelected) {
        // Deselect all filtered
        const filteredIds = this.filteredAgreements.map(a => a._id);
        this.selectedAgreements = this.selectedAgreements.filter(id => !filteredIds.includes(id));
      } else {
        // Select all filtered
        const filteredIds = this.filteredAgreements.map(a => a._id);
        const newSelection = [...new Set([...this.selectedAgreements, ...filteredIds])];
        this.selectedAgreements = newSelection;
      }
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        active: 'bg-green-100 text-green-700',
        active_old: 'bg-gray-100 text-gray-600',
        extended: 'bg-blue-100 text-blue-700',
        pending: 'bg-yellow-100 text-yellow-700',
        dismissed: 'bg-orange-100 text-orange-700',
        returned: 'bg-red-100 text-red-700',
        redrawn: 'bg-purple-100 text-purple-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-600';
    },
    
    getStatusLabel(status) {
      const labels = {
        active: this.$t('agreements.active'),
        active_old: this.$t('agreements.active_old') || 'Gammalt aktivt',
        extended: this.$t('agreements.extended'),
        pending: this.$t('agreements.pending'),
        dismissed: this.$t('agreements.dismissed'),
        returned: this.$t('agreements.returned'),
        redrawn: this.$t('agreements.redrawn')
      };
      if (!status) return this.$t('agreements.no_status');
      return labels[status] || status;
    },
    
    selectAllSuggested() {
      const suggestedIds = this.suggestedAutoExtend.map(a => a._id);
      const newSelection = [...new Set([...this.selectedAgreements, ...suggestedIds])];
      this.selectedAgreements = newSelection;
    },
    
    getExpiryBadgeClass(agreement) {
      if (!agreement.contract_start || !agreement.contract_period) {
        return 'bg-gray-100 text-gray-600';
      }
      
      const contractStart = new Date(agreement.contract_start);
      const periodMatch = agreement.contract_period.toString().match(/(\d+)/);
      if (!periodMatch) return 'bg-gray-100 text-gray-600';
      
      const periodMonths = parseInt(periodMatch[1]);
      const expiryDate = new Date(contractStart);
      expiryDate.setMonth(expiryDate.getMonth() + periodMonths);
      
      const now = new Date();
      
      if (expiryDate < now) {
        return 'bg-red-100 text-red-700'; // Already expired
      } else {
        return 'bg-amber-100 text-amber-700'; // Expiring soon
      }
    },
    
    getExpiryLabel(agreement) {
      if (!agreement.contract_start || !agreement.contract_period) {
        return this.$t('agreements.bulk.no_expiry_data');
      }
      
      const contractStart = new Date(agreement.contract_start);
      const periodMatch = agreement.contract_period.toString().match(/(\d+)/);
      if (!periodMatch) return this.$t('agreements.bulk.no_expiry_data');
      
      const periodMonths = parseInt(periodMatch[1]);
      const expiryDate = new Date(contractStart);
      expiryDate.setMonth(expiryDate.getMonth() + periodMonths);
      
      const now = new Date();
      
      if (expiryDate < now) {
        // Calculate how long ago it expired
        const monthsAgo = Math.floor((now - expiryDate) / (1000 * 60 * 60 * 24 * 30));
        if (monthsAgo === 0) {
          return this.$t('agreements.bulk.expired_recently');
        }
        return this.$t('agreements.bulk.expired_months_ago', { months: monthsAgo });
      } else {
        // Calculate how soon it expires
        const daysUntil = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        if (daysUntil <= 30) {
          return this.$t('agreements.bulk.expires_days', { days: daysUntil });
        }
        const monthsUntil = Math.ceil(daysUntil / 30);
        return this.$t('agreements.bulk.expires_months', { months: monthsUntil });
      }
    },
    
    getAlarmCount(agreement_number) {
      if (!this.agreementAlarms || this.agreementAlarms.length === 0) {
        return 0;
      }
      const alarms = this.agreementAlarms.filter(alarm => alarm.agreement_number === agreement_number);
      return alarms ? alarms.length : 0;
    },
    
    async checkAgreementsWithAlarms() {
      const sourceAgreements = this.allAgreements.length > 0 ? this.allAgreements : this.agreements;
      const selectedAgreementObjects = this.selectedAgreements
        .map(id => sourceAgreements.find(a => a._id === id))
        .filter(a => a);
      
      const agreementsWithAlarms = selectedAgreementObjects.filter(agreement => {
        const alarmCount = this.getAlarmCount(agreement.agreement_number);
        return alarmCount > 0;
      });
      
      if (agreementsWithAlarms.length > 0) {
        const agreementList = agreementsWithAlarms
          .map(a => `${a.agreement_number} (${this.getAlarmCount(a.agreement_number)} larm)`)
          .join(', ');
        
        return new Promise((resolve) => {
          this.$modal.show("dialog", {
            title: `<span class="text-sm font-semibold">${this.$t('agreements.bulk.warning_alarms_title')}</span>`,
            text: `<div class="text-sm">${this.$t('agreements.bulk.warning_alarms_text', { agreements: agreementList })}</div>`,
            buttons: [
              {
                title: `<div class="bg-gray-500 text-white text-sm font-sans py-2">${this.$t('user_actions.cancel')}</div>`,
                handler: () => {
                  this.$modal.hide("dialog");
                  resolve(false);
                },
              },
            ],
          });
        });
      }
      
      return true;
    },
    
    async dismissSelected() {
      if (!this.validateForm('dismiss')) {
        return;
      }
      
      // Check for agreements with alarms first
      const shouldContinue = await this.checkAgreementsWithAlarms();
      if (!shouldContinue) {
        return;
      }
      
      // Show final confirmation
      this.$modal.show("dialog", {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.bulk.confirm_dismiss', { count: this.selectedAgreements.length })}</span>`,
        buttons: [
          {
            title: `<div class="bg-gray-500 text-white text-sm font-sans py-2">${this.$t('user_actions.cancel')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-green-500 text-white text-sm font-sans py-2">${this.$t('user_actions.confirm')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
              this.processAgreements('dismiss');
            },
          },
        ],
      });
    },
    
    async endSelected() {
      if (!this.validateForm('end')) {
        return;
      }
      
      // Check for agreements with alarms first
      const shouldContinue = await this.checkAgreementsWithAlarms();
      if (!shouldContinue) {
        return;
      }
      
      this.$modal.show("dialog", {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.bulk.confirm_end', { count: this.selectedAgreements.length })}</span>`,
        buttons: [
          {
            title: `<div class="bg-gray-500 text-white text-sm font-sans py-2">${this.$t('user_actions.cancel')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('user_actions.confirm')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
              this.processAgreements('end');
            },
          },
        ],
      });
    },
    
    async extendSelected() {
      this.$modal.show("dialog", {
        title: `<span class="text-sm font-semibold">${this.$t('agreements.bulk.confirm_extend', { count: this.selectedAgreements.length })}</span>`,
        buttons: [
          {
            title: `<div class="bg-gray-500 text-white text-sm font-sans py-2">${this.$t('user_actions.cancel')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-green-500 text-white text-sm font-sans py-2">${this.$t('user_actions.confirm')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
              this.processAgreements('extend');
            },
          },
        ],
      });
    },
    
    async processAgreements(action) {
      this.isProcessing = true;
      this.processedCount = 0;
      
      try {
        let endpoint = '';
        let response;
        
        switch (action) {
          case 'dismiss': {
            // Use bulk dismiss endpoint
            endpoint = `${process.env.VUE_APP_SERVER_URL}/agreements/bulk-dismiss`;
            response = await this.axios.post(endpoint, {
              agreement_ids: this.selectedAgreements,
              dismissal_date: this.actionDate,
              billing_method: this.billingMethod,
              dismissal_pdf: this.selectedFile ? this.selectedFile.name : null
            });
            this.processedCount = response.data.processedCount || this.selectedAgreements.length;
            break;
          }
          case 'end': {
            // Use bulk cancel endpoint
            endpoint = `${process.env.VUE_APP_SERVER_URL}/agreements/bulk-cancel`;
            response = await this.axios.post(endpoint, {
              agreement_ids: this.selectedAgreements,
              cancellation_date: this.actionDate,
              billing_method: this.billingMethod,
              cancellation_pdf: this.selectedFile ? this.selectedFile.name : null,
              lost_equipment_notes: this.notes.trim() || null
            });
            this.processedCount = response.data.processedCount || this.selectedAgreements.length;
            break;
          }
          case 'extend': {
            // Use the create-extended endpoint with all selected agreements at once
            const sourceAgreements = this.allAgreements.length > 0 ? this.allAgreements : this.agreements;
            const agreementsToExtend = this.selectedAgreements
              .map(id => sourceAgreements.find(a => a._id === id))
              .filter(a => a);
            
            if (agreementsToExtend.length > 0) {
              endpoint = `${process.env.VUE_APP_SERVER_URL}/agreements/create-extended`;
              response = await this.axios.post(endpoint, {
                agreements: agreementsToExtend
              });
              this.processedCount = response.data.processedCount || agreementsToExtend.length;
            }
            break;
          }
        }
        
        this.isProcessing = false;
        this.handleSuccess(this.$t('agreements.bulk.success', { count: this.processedCount }));
        
        // Remove only the processed agreements from the list and selection, keep form data for next batch
        const processedIds = [...this.selectedAgreements];
        
        // Remove processed agreements from the allAgreements list so they don't show anymore
        this.allAgreements = this.allAgreements.filter(a => !processedIds.includes(a._id));
        
        // Clear selection but keep form data intact
        this.selectedAgreements = [];
        
        // Emit update to refresh the parent's list
        this.$emit('agreements-updated');
        
      } catch (error) {
        console.error(`Failed to process agreements:`, error);
        this.isProcessing = false;
        this.handleError(null, this.$t('agreements.bulk.some_failed', { count: this.selectedAgreements.length }));
      }
    }
  }
};
</script>

<style scoped>
.max-h-96 {
  max-height: 24rem;
}
</style>